// Angular imports
import { Injectable, inject } from '@angular/core';

// Project libraries
import { ContactTypeString } from '@tgx/shared/enums';
import {
  BillingAccountContactCreateInput,
  BodyContactData,
  ContactModification,
  ContactTypeData,
} from '@tgx/shared/interfaces';
import { PlatformInfoService, ProfilesService, URL_NETWORK_SERVICE } from '@tgx/shared/services';
import { firstValueFrom } from 'rxjs';

// Local imports
import { Partner } from '../interface/billing-account';
import { ContactInfoData, ContactSectionParamData } from '../interface/user-profile-contacts-info';
import { OnboardingBillingService } from './onboarding-billing.service';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class OnboardingContactsService {
  private http = inject(HttpClient);
  private profileService = inject(ProfilesService);
  private billingService = inject(OnboardingBillingService);
  private platformInfoService = inject(PlatformInfoService);

  private isBuyer = false;
  private isSeller = false;
  private selectedOrgCode: string = null;

  private urlNetworkService = inject(URL_NETWORK_SERVICE);

  constructor() {
    this.platformInfoService.organizationSelected$.subscribe({
      next: (org) => {
        if (org) {
          this.isBuyer = org.isBuyer;
          this.isSeller = org.isSeller;
          this.selectedOrgCode = org.code;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  async addBillingContact(billingPartner: Partner, billingContact: ContactInfoData): Promise<void> {
    try {
      const accounts = billingPartner?.BillingAccounts || [];
      const billingAccountContacts = await this.billingService.retrieveBillingAccountContacts(this.selectedOrgCode);

      if (accounts.length === 0) {
        throw new Error('Cannot create billing contact because there is no billing account');
      }

      const promises = accounts.map(async (account) => {
        const contactExists = billingAccountContacts.some((contact) => contact.email === billingContact.email);

        if (!contactExists) {
          const billingContactData: BillingAccountContactCreateInput = {
            email: billingContact.email,
            receiveInvoices: true,
            BillingAccount: { connect: { id: account.id } },
          };

          return this.billingService.createBillingContact(billingContactData);
        }

        return null;
      });

      await Promise.all(promises);
    } catch (error: any) {
      throw new Error(`Error creating billing contact: ${this.extractErrorMessage(error)}`, { cause: error });
    }
  }

  private extractErrorMessage(error: any): string {
    if (!error) {
      return 'Unknown billing error';
    }

    if (typeof error === 'string') {
      return error;
    }

    const graphQlMessage = error?.errors?.[0]?.message;
    if (graphQlMessage) {
      return graphQlMessage;
    }

    const nestedGraphQlMessage = error?.error?.errors?.[0]?.message;
    if (nestedGraphQlMessage) {
      return nestedGraphQlMessage;
    }

    return error.message || JSON.stringify(error);
  }

  async onAddCompanyContacts(contactSection: ContactSectionParamData): Promise<void> {
    try {
      const body = await this.generateBodyCompanyContact(contactSection);
      if (body) {
        await this.profileService.updateCompanyContacts(body);
      }
    } catch (error: any) {
      throw `·Error create company notification: ${error.message || error}`;
    }
  }

  private async generateBodyCompanyContact(contacts: ContactSectionParamData): Promise<BodyContactData> {
    const companyContacts = await this.profileService.getCompanyContacts(this.selectedOrgCode);

    const contactsData: BodyContactData = { ownerCode: this.selectedOrgCode, data: {} };

    const formBuyerContacts = {
      'API Implementation': {
        contactType: ContactTypeString.API_IMPLEMENTATION,
        email: contacts.commercialDecisionContact.email,
      },
      'Tech Support': { contactType: ContactTypeString.TECH_SUPPORT, email: contacts.techDecisionContact.email },
      'Activation': { contactType: ContactTypeString.ACTIVATION, email: contacts.techSupportContact.email },
    };

    const formSellerContacts = {
      'API Implementation': {
        contactType: ContactTypeString.API_IMPLEMENTATION,
        email: contacts.commercialDecisionContact.email,
      },
      'Tech Support': { contactType: ContactTypeString.TECH_SUPPORT, email: contacts.techDecisionContact.email },
      'Activation': { contactType: ContactTypeString.ACTIVATION, email: contacts.techSupportContact.email },
    };

    if (this.isBuyer) {
      const buyerContacts: ContactTypeData[] = companyContacts?.buyerKeyContacts || [];
      const buyerUpdates: ContactModification = this.generateAddDeleteCompanyContact(buyerContacts, formBuyerContacts);

      if (Object.keys(buyerUpdates).length > 0) {
        contactsData.data['buyerKeyContactsInput'] = buyerUpdates;
      }
    }

    if (this.isSeller) {
      const sellerContacts: ContactTypeData[] = companyContacts?.sellerKeyContacts || [];
      const sellerUpdates: ContactModification = this.generateAddDeleteCompanyContact(
        sellerContacts,
        formSellerContacts,
      );

      if (Object.keys(sellerUpdates).length > 0) {
        contactsData.data['sellerKeyContactsInput'] = sellerUpdates;
      }
    }

    if (Object.keys(contactsData.data).length === 0) {
      return null;
    }

    return contactsData;
  }

  // generate data array add and delete company contacts
  private generateAddDeleteCompanyContact(
    existingContacts: any[],
    formContacts: { [key: string]: { contactType: string; email: string } },
  ): ContactModification {
    const add: ContactTypeData[] = [];
    const remove: ContactTypeData[] = [];

    for (const key in formContacts) {
      const formContact = formContacts[key];
      const filteredContact = existingContacts.filter((contact) => contact.contactType === formContact.contactType);

      if (filteredContact.length === 0) {
        add.push(formContact);
      } else {
        const foundContact = filteredContact.find((contact) => contact.email === formContact.email);
        if (!foundContact) {
          filteredContact.forEach((contact) => {
            remove.push({ contactType: contact.contactType, email: contact.email });
          });
          add.push(formContact);
        }
      }
    }

    const result: ContactModification = {};
    if (add.length > 0) {
      result.add = add;
    }
    if (remove.length > 0) {
      result.delete = remove;
    }

    return result;
  }

  async retrieveSignerContact(crmID: string): Promise<string> {
    const url = `${this.urlNetworkService}crm/company-commercial-contact?companyCrmId=${crmID}`;

    const response = await firstValueFrom(this.http.get<string>(url));
    const contact = response?.['commercial_contact'] || '';
    return contact.trim().toLowerCase();
  }
}
